export const GUARANTOR_CATEGORY_OPTIONS = [
  { value: "document", label: "Document upload" },
  { value: "profile", label: "Profile field" },
  { value: "consent", label: "Consent" },
];

export const GUARANTOR_DOCUMENT_TYPE_OPTIONS = [
  { value: "national_id", label: "National ID" },
  { value: "passport_photo", label: "Passport photo" },
  { value: "salary_slip", label: "Salary slip" },
  { value: "bank_statement", label: "Bank statement" },
  { value: "guarantor_form", label: "Guarantor form" },
  { value: "business_license", label: "Business license" },
  { value: "other", label: "Other" },
];

export const GUARANTOR_PROFILE_FIELD_OPTIONS = [
  { value: "full_name", label: "Full name" },
  { value: "phone", label: "Phone number" },
  { value: "nida", label: "National ID (NIDA)" },
];

export const GUARANTOR_CONSENT_TYPE_OPTIONS = [
  { value: "e_consent", label: "E-consent (CRB-004)" },
  { value: "liability_disclosure", label: "Liability disclosure" },
];

export const DEFAULT_GUARANTOR_REQUIREMENT_FORM = {
  key: "",
  label: "",
  description: "",
  category: "document",
  document_type: "national_id",
  profile_field: "full_name",
  consent_type: "e_consent",
  is_required: true,
  is_active: true,
  sort_order: 0,
  requirement_group: "",
};

const CATEGORY_LABELS = Object.fromEntries(GUARANTOR_CATEGORY_OPTIONS.map((o) => [o.value, o.label]));
const DOC_LABELS = Object.fromEntries(GUARANTOR_DOCUMENT_TYPE_OPTIONS.map((o) => [o.value, o.label]));
const PROFILE_LABELS = Object.fromEntries(GUARANTOR_PROFILE_FIELD_OPTIONS.map((o) => [o.value, o.label]));
const CONSENT_LABELS = Object.fromEntries(GUARANTOR_CONSENT_TYPE_OPTIONS.map((o) => [o.value, o.label]));

export function categoryLabel(value) {
  return CATEGORY_LABELS[value] || value;
}

export function requirementDetail(row) {
  if (row.category === "document" && row.document_type) {
    return DOC_LABELS[row.document_type] || row.document_type;
  }
  if (row.category === "profile" && row.profile_field) {
    return PROFILE_LABELS[row.profile_field] || row.profile_field;
  }
  if (row.category === "consent" && row.consent_type) {
    return CONSENT_LABELS[row.consent_type] || row.consent_type;
  }
  return "—";
}

export function requirementToForm(row) {
  if (!row) {
    return { ...DEFAULT_GUARANTOR_REQUIREMENT_FORM };
  }
  return {
    key: row.key || "",
    label: row.label || "",
    description: row.description || "",
    category: row.category || "document",
    document_type: row.document_type || "national_id",
    profile_field: row.profile_field || "full_name",
    consent_type: row.consent_type || "e_consent",
    is_required: row.is_required !== false,
    is_active: row.is_active !== false,
    sort_order: row.sort_order || 0,
    requirement_group: row.requirement_group || "",
  };
}

function cleanOptional(value) {
  const trimmed = String(value || "").trim();
  return trimmed || null;
}

export function buildCreatePayload(form) {
  const payload = {
    key: form.key.trim().toLowerCase().replace(/\s+/g, "_"),
    label: form.label.trim(),
    description: cleanOptional(form.description),
    category: form.category,
    is_required: Boolean(form.is_required),
    is_active: Boolean(form.is_active),
    sort_order: Number(form.sort_order) || 0,
    requirement_group: cleanOptional(form.requirement_group),
  };
  if (form.category === "document") {
    payload.document_type = form.document_type;
  } else if (form.category === "profile") {
    payload.profile_field = form.profile_field;
  } else if (form.category === "consent") {
    payload.consent_type = form.consent_type;
  }
  return payload;
}

export function buildUpdatePayload(form) {
  const payload = buildCreatePayload(form);
  delete payload.key;
  return payload;
}

export function slugifyKey(label) {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 62);
}

export function activeDocumentRequirements(requirements) {
  return (requirements || [])
    .filter((row) => row.is_active && row.category === "document")
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
}

export function activeProfileRequirements(requirements) {
  return (requirements || [])
    .filter((row) => row.is_active && row.category === "profile")
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
}

export function activeConsentRequirements(requirements) {
  return (requirements || [])
    .filter((row) => row.is_active && row.category === "consent")
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
}

export function profileFieldFormKey(profileField) {
  if (profileField === "nida") {
    return "nida_number";
  }
  return profileField;
}

export function validateGuarantorForm(form, requirements, documentFiles = {}) {
  const missing = [];
  for (const req of requirements || []) {
    if (!req.is_active || !req.is_required) {
      continue;
    }
    if (req.category === "document") {
      const file = documentFiles[req.document_type];
      if (!file) {
        missing.push(req.label);
      }
    } else if (req.category === "profile") {
      const key = profileFieldFormKey(req.profile_field);
      const value = form[key];
      if (!value || (typeof value === "string" && !value.trim())) {
        missing.push(req.label);
      }
    } else if (req.category === "consent") {
      if (req.consent_type === "e_consent" && !form.consent_granted) {
        missing.push(req.label);
      }
      if (req.consent_type === "liability_disclosure" && !form.liability_disclosure_ack) {
        missing.push(req.label);
      }
    }
  }
  return missing;
}
